import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAccountDashboardData, type OpportunityRecord } from '../hooks/useAccountDashboardData';
import SearchableSelect from '../components/SearchableSelect';
import { formatNumber, formatCurrency } from '../utils/formatters';
import { exportToCSV } from '../utils/csv';
import type { AccountSegment } from '../types/segment';

// Columns for the "Export CSV" of the filtered firm rows.
const ACCOUNT_EXPORT_COLUMNS = [
  { header: 'Firm Name', accessorKey: 'Name' },
  { header: 'Firm ID', accessorKey: 'EEP_Firm_ID__c' },
  { header: 'Affiliation Code', accessorKey: 'EEP_Affiliation_Code__c' },
  { header: 'Firm Created Date', accessorKey: 'EEP_Account_Created_Date__c' },
  { header: 'Cohort Year', accessorKey: 'cohortYear' },
  { header: 'Total Advisors', accessorKey: 'EEP_Total_Advisors__c' },
  { header: 'Total Plans Sold', accessorKey: 'Total_Plans_Sold__c' },
  { header: 'Total Plan Summaries Sold', accessorKey: 'EEP_Total_Plan_Summaries_Sold__c' },
  { header: 'Total Revenue', accessorKey: 'Total_Revenue__c' },
  { header: 'First Plan Date', accessorKey: 'First_Plan_Date__c' },
  { header: 'Second Plan Date', accessorKey: 'Second_Plan_Date__c' },
  { header: 'Seventh Plan Date', accessorKey: 'Seventh_Plan_Date__c' },
  { header: 'Last Plan Date', accessorKey: 'Last_Plan_Date__c' },
  { header: 'Last Spend Date', accessorKey: 'Last_Spend_Date__c' },
  { header: 'Trust-Based Credits', accessorKey: 'EEP_Trust_Based_Plan_Credits__c' },
  { header: 'Will-Based Credits', accessorKey: 'EEP_Will_Based_Plan_Credits__c' },
  { header: 'Plan Summary Credits', accessorKey: 'Plan_Summary_Plan_Credits__c' },
  { header: 'Incomplete Plans', accessorKey: 'Number_of_Incomplete_Plans__c' },
  { header: 'Firm CRD', accessorKey: 'Firm_CRD__c' },
  { header: 'Website', accessorKey: 'Website__c' },
  { header: 'Churn Date', accessorKey: 'Churn_Date__c' },
  { header: 'Churn Reason', accessorKey: 'Churn_Reason__c' },
];

type BreakdownDimension = 'planType' | 'advisor' | 'year' | 'cohort';

const DIMENSION_LABELS: Record<BreakdownDimension, string> = {
  planType: 'Plan Type',
  advisor: 'Advisor',
  year: 'Year',
  cohort: 'Account Cohort',
};

function buildChartData(
  opps: OpportunityRecord[],
  dimension: BreakdownDimension,
  yearField: 'planCreatedDate' | 'closeDate'
): { name: string; count: number }[] {
  const map = new Map<string, number>();
  for (const opp of opps) {
    let key: string;
    if (dimension === 'planType') {
      key = opp.planType || 'Unknown';
    } else if (dimension === 'advisor') {
      key = opp.advisorName;
    } else if (dimension === 'year') {
      const dateStr = yearField === 'planCreatedDate' ? opp.planCreatedDate : opp.closeDate;
      key = dateStr ? String(new Date(dateStr).getFullYear()) : 'Unknown';
    } else {
      key = opp.cohortYear || 'Unknown';
    }
    map.set(key, (map.get(key) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

interface MetricCardProps {
  title: string;
  value: number;
  opps: OpportunityRecord[];
  yearField?: 'planCreatedDate' | 'closeDate';
  yearDimLabel?: string;
}

function MetricCard({ title, value, opps, yearField = 'closeDate', yearDimLabel = 'Completed Year' }: MetricCardProps) {
  const [dim, setDim] = useState<BreakdownDimension>('planType');

  const chartData = useMemo(
    () => buildChartData(opps, dim, yearField),
    [opps, dim, yearField]
  );

  const dimTabs: { key: BreakdownDimension; label: string }[] = [
    { key: 'planType', label: 'Plan Type' },
    { key: 'advisor', label: 'Advisor' },
    { key: 'year', label: yearDimLabel },
    { key: 'cohort', label: 'Account Cohort' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5 pb-3">
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{formatNumber(value)}</p>
      </div>

      <div className="border-t border-gray-100 px-5 pb-5">
        <div className="flex gap-1 mt-3 mb-3 flex-wrap">
          {dimTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setDim(tab.key)}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                dim === tab.key
                  ? 'bg-eep-navy text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No data</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 32)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 11 }}
                width={140}
              />
              <Tooltip />
              <Bar dataKey="count" fill="#2D3250" radius={[0, 4, 4, 0]} name={DIMENSION_LABELS[dim]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function UsersCard({ value, opps }: { value: number; opps: OpportunityRecord[] }) {
  const chartData = useMemo(() => {
    const seen = new Set<string>();
    const map = new Map<string, number>();
    for (const opp of opps) {
      const key = `${opp.advisorName}||${opp.userType || 'Unknown'}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const type = opp.userType || 'Unknown';
      map.set(type, (map.get(type) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [opps]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5 pb-3">
        <p className="text-sm font-medium text-gray-500 mb-1">Users</p>
        <p className="text-3xl font-bold text-gray-900">{formatNumber(value)}</p>
      </div>
      <div className="border-t border-gray-100 px-5 pb-5">
        <p className="text-xs font-medium text-gray-400 mt-3 mb-3">User Type</p>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No data</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 32)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
              <Tooltip />
              <Bar dataKey="count" fill="#2D3250" radius={[0, 4, 4, 0]} name="Users" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function DaysCard({ title, days, accountCount }: { title: string; days: number | null; accountCount: number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
      {days === null ? (
        <p className="text-2xl font-bold text-gray-400">—</p>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900">{days} <span className="text-lg font-medium text-gray-500">days</span></p>
          <p className="text-xs text-gray-400 mt-1">avg across {accountCount} {accountCount === 1 ? 'account' : 'accounts'}</p>
        </>
      )}
    </div>
  );
}

export default function AccountDashboard() {
  const { accounts, opportunities, loading, error, refetch } = useAccountDashboardData();

  const [selectedAccountNames, setSelectedAccountNames] = useState<string[]>([]);
  const [selectedFirmId, setSelectedFirmId] = useState<string>('');
  const [selectedAffiliationCode, setSelectedAffiliationCode] = useState<string>('');
  const [selectedCohortYear, setSelectedCohortYear] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<AccountSegment>('all');

  // Filter options derived from accounts
  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.Name, label: a.Name, id: a.Id })),
    [accounts]
  );

  const affiliationCodeOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const a of accounts) {
      if (a.EEP_Affiliation_Code__c && !seen.has(a.EEP_Affiliation_Code__c)) {
        seen.add(a.EEP_Affiliation_Code__c);
        opts.push({ value: a.EEP_Affiliation_Code__c, label: a.EEP_Affiliation_Code__c });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [accounts]);

  const firmIdOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const a of accounts) {
      if (a.EEP_Firm_ID__c && !seen.has(a.EEP_Firm_ID__c)) {
        seen.add(a.EEP_Firm_ID__c);
        opts.push({ value: a.EEP_Firm_ID__c, label: a.EEP_Firm_ID__c });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [accounts]);

  const cohortYearOptions = useMemo(() => {
    const years = new Set<string>();
    for (const a of accounts) {
      if (a.cohortYear) years.add(a.cohortYear);
    }
    return Array.from(years)
      .sort((a, b) => b.localeCompare(a))
      .map((y) => ({ value: y, label: y }));
  }, [accounts]);

  // Filtered accounts matching all active filters
  const filteredAccountIds = useMemo(() => {
    const matching = accounts.filter((a) => {
      if (selectedAccountNames.length > 0 && !selectedAccountNames.includes(a.Name)) return false;
      if (selectedFirmId && a.EEP_Firm_ID__c !== selectedFirmId) return false;
      if (selectedAffiliationCode && a.EEP_Affiliation_Code__c !== selectedAffiliationCode) return false;
      if (selectedCohortYear && a.cohortYear !== selectedCohortYear) return false;
      if (selectedSegment === 'onboarded' && ((a.Total_Plans_Sold__c ?? 0) < 7)) return false;
      if (selectedSegment === 'onboarding' && ((a.Total_Plans_Sold__c ?? 0) >= 7)) return false;
      return true;
    });
    return new Set(matching.map((a) => a.Id));
  }, [accounts, selectedAccountNames, selectedFirmId, selectedAffiliationCode, selectedCohortYear, selectedSegment]);

  const hasFilters = selectedAccountNames.length > 0 || selectedFirmId || selectedAffiliationCode || selectedCohortYear || selectedSegment !== 'all';

  // Matched accounts count
  const matchedAccountsCount = filteredAccountIds.size;

  // Advisor count from accounts
  const advisorCount = useMemo(() => {
    return accounts
      .filter((a) => filteredAccountIds.has(a.Id))
      .reduce((sum, a) => sum + (a.EEP_Total_Advisors__c || 0), 0);
  }, [accounts, filteredAccountIds]);

  // Average Lifetime Value across filtered accounts
  const avgLTV = useMemo(() => {
    const filtered = accounts.filter((a) => filteredAccountIds.has(a.Id));
    const withRevenue = filtered.filter((a) => a.Total_Revenue__c != null && a.Total_Revenue__c > 0);
    if (withRevenue.length === 0) return { avg: 0, count: 0 };
    const total = withRevenue.reduce((sum, a) => sum + a.Total_Revenue__c!, 0);
    return { avg: total / withRevenue.length, count: withRevenue.length };
  }, [accounts, filteredAccountIds]);

  // Filter opportunities to matching accounts
  const filteredOpps = useMemo(() => {
    if (!hasFilters) return opportunities;
    return opportunities.filter((o) => filteredAccountIds.has(o.AccountId));
  }, [opportunities, filteredAccountIds, hasFilters]);

  // KPI computations
  const metrics = useMemo(() => {
    const plansCreatedOpps = filteredOpps.filter((o) => o.planCreatedDate !== null);
    const plansCompletedOpps = filteredOpps.filter((o) => o.isWon);
    const amendmentOpps = plansCompletedOpps.filter((o) => o.isAmendment);
    const notaryOpps = plansCompletedOpps.filter((o) => o.includeNotary);
    const attorneyOpps = plansCompletedOpps.filter((o) => o.includeAttorneyLetter || o.includeAttorneyCall);
    const printBindOpps = plansCompletedOpps.filter((o) => o.includePrintBind);

    return {
      plansCreated: plansCreatedOpps.length,
      plansCompleted: plansCompletedOpps.length,
      amendments: amendmentOpps.length,
      notary: notaryOpps.length,
      attorney: attorneyOpps.length,
      printBind: printBindOpps.length,
      plansCreatedOpps,
      plansCompletedOpps,
      amendmentOpps,
      notaryOpps,
      attorneyOpps,
      printBindOpps,
    };
  }, [filteredOpps]);

  // Time-to-plan metrics
  const timeMetrics = useMemo(() => {
    // Group plans by account, sorted by planCreatedDate
    const plansByAccount = new Map<string, Date[]>();
    for (const opp of filteredOpps) {
      if (!opp.planCreatedDate) continue;
      const d = new Date(opp.planCreatedDate);
      if (!plansByAccount.has(opp.AccountId)) plansByAccount.set(opp.AccountId, []);
      plansByAccount.get(opp.AccountId)!.push(d);
    }
    for (const dates of plansByAccount.values()) {
      dates.sort((a, b) => a.getTime() - b.getTime());
    }

    const accountCreatedMap = new Map<string, Date>();
    for (const acct of accounts) {
      if (filteredAccountIds.has(acct.Id) && acct.EEP_Account_Created_Date__c) {
        accountCreatedMap.set(acct.Id, new Date(acct.EEP_Account_Created_Date__c));
      }
    }

    let totalSeventh = 0, countSeventh = 0;

    for (const [accountId, dates] of plansByAccount.entries()) {
      const created = accountCreatedMap.get(accountId);
      if (!created) continue;
      if (dates.length >= 7) {
        const days = (dates[6].getTime() - created.getTime()) / 86400000;
        if (days >= 0) { totalSeventh += days; countSeventh++; }
      }
    }

    return {
      avgDaysToSeventh: countSeventh > 0 ? Math.round(totalSeventh / countSeventh) : null,
      countSeventh,
    };
  }, [filteredOpps, accounts, filteredAccountIds]);

  // Filtered firm rows (deduped) for CSV export.
  const filteredFirms = useMemo(
    () => accounts.filter((a) => filteredAccountIds.has(a.Id)),
    [accounts, filteredAccountIds]
  );

  function handleExport() {
    exportToCSV(
      filteredFirms as unknown as Record<string, unknown>[],
      ACCOUNT_EXPORT_COLUMNS,
      'account_dashboard'
    );
  }

  function clearFilters() {
    setSelectedAccountNames([]);
    setSelectedFirmId('');
    setSelectedAffiliationCode('');
    setSelectedCohortYear('');
    setSelectedSegment('all');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-eep-navy rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Filter accounts to analyze data</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={filteredFirms.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-eep-navy rounded-lg hover:bg-eep-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export the filtered firms to CSV"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={refetch}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Account Name</label>
            <SearchableSelect
              options={accountOptions}
              value={selectedAccountNames}
              onChange={(v) => setSelectedAccountNames(v as string[])}
              placeholder="All accounts"
              multiSelect
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Firm ID</label>
            <SearchableSelect
              options={firmIdOptions}
              value={selectedFirmId}
              onChange={(v) => setSelectedFirmId(v as string)}
              placeholder="All firm IDs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Affiliation Code</label>
            <SearchableSelect
              options={affiliationCodeOptions}
              value={selectedAffiliationCode}
              onChange={(v) => setSelectedAffiliationCode(v as string)}
              placeholder="All affiliations"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Account Created Year</label>
            <SearchableSelect
              options={cohortYearOptions}
              value={selectedCohortYear}
              onChange={(v) => setSelectedCohortYear(v as string)}
              placeholder="All years"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Account Segment</label>
            <select
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value as AccountSegment)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-eep-navy focus:ring-1 focus:ring-eep-navy"
            >
              <option value="all">All Accounts</option>
              <option value="onboarding">Onboarding (0-6 Plans)</option>
              <option value="onboarded">Onboarded (7+ Plans)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Filter summary */}
      <div className="flex items-center gap-3 mb-6 text-sm text-gray-500">
        <span>
          Showing{' '}
          <span className="font-semibold text-gray-900">{formatNumber(matchedAccountsCount)}</span>{' '}
          {matchedAccountsCount === 1 ? 'account' : 'accounts'}
        </span>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-eep-navy hover:underline font-medium text-xs"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <MetricCard
          title="Total Plans Created"
          value={metrics.plansCreated}
          opps={metrics.plansCreatedOpps}
          yearField="planCreatedDate"
          yearDimLabel="Created Year"
        />
        <MetricCard
          title="Total Plans Completed"
          value={metrics.plansCompleted}
          opps={metrics.plansCompletedOpps}
          yearField="closeDate"
          yearDimLabel="Completed Year"
        />
        <UsersCard value={advisorCount} opps={filteredOpps} />
        <MetricCard
          title="Number of Amendments"
          value={metrics.amendments}
          opps={metrics.amendmentOpps}
          yearField="closeDate"
          yearDimLabel="Completed Year"
        />
        <MetricCard
          title="Notary Services Purchased"
          value={metrics.notary}
          opps={metrics.notaryOpps}
          yearField="closeDate"
          yearDimLabel="Completed Year"
        />
        <MetricCard
          title="Attorney Reviews Purchased"
          value={metrics.attorney}
          opps={metrics.attorneyOpps}
          yearField="closeDate"
          yearDimLabel="Completed Year"
        />
        <MetricCard
          title="Notary Binders Purchased"
          value={metrics.printBind}
          opps={metrics.printBindOpps}
          yearField="closeDate"
          yearDimLabel="Completed Year"
        />
        <DaysCard
          title="Avg. Days to 7th Plan"
          days={timeMetrics.avgDaysToSeventh}
          accountCount={timeMetrics.countSeventh}
        />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <p className="text-sm font-medium text-gray-500 mb-1">Avg. Lifetime Value</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(avgLTV.avg)}</p>
          <p className="text-xs text-gray-400 mt-1">across {avgLTV.count} {avgLTV.count === 1 ? 'account' : 'accounts'} with revenue</p>
        </div>
      </div>
    </div>
  );
}
